import { HttpResponse } from '@angular/common/http';
import { AfterViewInit, Component, Inject, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FileInfo } from '@vendasta/galaxy/uploader';
import { omit } from 'lodash';
import { Observable } from 'rxjs';
import { googleFonts } from '../../common/fonts';
import { Workflow } from '../../common/review-types';
import { ApiRoutesV1 } from '../../common/routes';
import { Validator } from '../../common/validator';
import { AjaxData } from '../../core/ajax.service';
import { CustomerVoiceRouterService } from '../../core/cv-router.service';
import { RichEditorComponent } from '../../shared/rich-editor/rich-editor.component';
import { ReviewPageUrlsService } from '../review-page-urls/review-page-urls.service';
import { EmailSettings, EmailWorkflowService } from './email-workflow.service';

@Component({
  selector: 'app-email-settings',
  templateUrl: './email-settings.component.html',
  styleUrls: ['./email-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EmailSettingsComponent implements OnInit, AfterViewInit {
  @Input() emailRequestType: Workflow;
  @ViewChild('messageBodyEditor') messageBodyEditor: RichEditorComponent;
  @ViewChild('secondaryMessageBodyEditor')
  secondaryMessageBodyEditor: RichEditorComponent;
  @ViewChild('directFeedbackMessageEditor')
  directFeedbackMessageEditor: RichEditorComponent;

  editEmailSettingsForm: UntypedFormGroup;
  isSaving = false;
  hasMyListing = false;
  aspects = [
    {
      label: 'Free',
      value: 0,
    },
    {
      label: '280:44',
      value: 280 / 44,
      selected: true,
    },
  ];

  emailSettings: EmailSettings = {
    sendFromAddress: '',
    emailSubject: '',
    buttonText: '',
    buttonColor: '',
    buttonUrl: '',
    emailGreeting: '',
    emailBody: '',
    secondaryMessageBody: '',
    emailTitle: '',
    logoUrl: null,
    headerUrl: null,
    emailTitleFont: '',
    emailBodyFont: '',
    includeDirectFeedbackOption: false,
    directFeedbackMessage: '',
  };

  directFeedbackOptionDisplay = 'none';

  greetings: string[];

  fonts: string[] = googleFonts;

  constructor(
    private emailWorkflowService: EmailWorkflowService,
    private fb: UntypedFormBuilder,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    private customerVoiceRouter: CustomerVoiceRouterService,
    private translateService: TranslateService,
    private reviewUrlService: ReviewPageUrlsService,
  ) {}

  ngOnInit(): void {
    this.editEmailSettingsForm = this.fb.group({
      sendFromAddress: ['', [Validator.isEmail]],
      emailSubject: '',
      buttonText: '',
      buttonColor: '',
      buttonUrl: ['', [Validator.isUrl]],
      emailGreeting: '',
      emailBody: '',
      secondaryMessageBody: '',
      emailTitle: '',
      emailTitleFont: '',
      emailBodyFont: '',
      includeDirectFeedbackOption: false,
      directFeedbackMessage: '',
    });

    this.editEmailSettingsForm.valueChanges.subscribe((emailSettings: EmailSettings) => {
      this.directFeedbackOptionDisplay = emailSettings.includeDirectFeedbackOption ? 'block' : 'none';
    });

    this.emailWorkflowService.getGreetingSelection().subscribe((greetings) => {
      this.greetings = greetings;
    });

    this.reviewUrlService.hasMyListing().subscribe((hasMyListing: boolean) => {
      this.hasMyListing = hasMyListing;
    });
  }

  ngAfterViewInit(): void {
    this.emailWorkflowService.getEmailSettings(this.emailRequestType).subscribe((emailSettings) => {
      this.emailSettings = emailSettings;
      this.editEmailSettingsForm.setValue(omit(emailSettings, ['headerUrl', 'logoUrl']));
      this.messageBodyEditor.clearAndInsertText(emailSettings.emailBody);
      this.secondaryMessageBodyEditor.clearAndInsertText(emailSettings.secondaryMessageBody);
      if (this.emailRequestType === Workflow.External) {
        this.directFeedbackMessageEditor.clearAndInsertText(emailSettings.directFeedbackMessage);
      }
    });
  }

  submitEmailSettingsForm(): void {
    if (this.editEmailSettingsForm.valid) {
      this.isSaving = true;
      this.emailWorkflowService
        .saveEmailSettings(
          this.editEmailSettingsForm.value,
          this.emailRequestType,
          this.emailSettings.headerUrl,
          this.emailSettings.logoUrl,
        )
        .subscribe({
          next: () => {
            this.snackbarService.successSnack(
              this.translateService.instant('SETTINGS.EMAIL_SETTINGS.CONTENT_FORM.SUCCESS'),
            );
            this.isSaving = false;
            this.previewEmail();
          },
          error: () => {
            this.snackbarService.errorSnack(
              this.translateService.instant('SETTINGS.EMAIL_SETTINGS.CONTENT_FORM.ERROR'),
            );
            this.isSaving = false;
          },
        });
    } else {
      this.snackbarService.errorSnack(this.translateService.instant('COMMON.VALIDATIONS.VALIDATION_ERRORS'));
    }
  }

  returnSettingsToDefaults(): void {
    this.emailWorkflowService.getEmailSettingDefaults(this.emailRequestType).subscribe((defaults: EmailSettings) => {
      this.emailSettings = defaults;
      this.editEmailSettingsForm.setValue(omit(defaults, ['headerUrl', 'logoUrl']));
      this.messageBodyEditor.clearAndInsertText(defaults.emailBody);
      this.secondaryMessageBodyEditor.clearAndInsertText(defaults.secondaryMessageBody);
      if (this.emailRequestType === Workflow.External) {
        this.directFeedbackMessageEditor.clearAndInsertText(defaults.directFeedbackMessage);
      }
    });
  }

  previewEmail(): void {
    //  Preview will not work for follow-up email at this time - no way to tell preview component which preview to call
    const dialogRef = this.dialog.open(EmailPreviewDialogComponent, {
      width: '800px',
    });
    const instance = dialogRef.componentInstance;
    instance.emailType = this.emailRequestType;
  }

  selectHeaderImage(): void {
    this.imageUploadUrl$.subscribe((imageUploadUrl) => {
      this.dialog.open(SelectHeaderDialogComponent, {
        data: {
          uploadUrl: imageUploadUrl,
          selectHeader: (url: string) => {
            this.emailSettings.headerUrl = url;
          },
        },
        width: '1000px',
        height: '600px',
      });
    });
  }

  deleteHeaderImage(): void {
    this.emailSettings.headerUrl = null;
  }

  getFormError(formGroup: UntypedFormGroup): string {
    return Validator.getFormError(formGroup);
  }

  get imageUploadUrl$(): Observable<string> {
    return this.customerVoiceRouter.getRouteUrl(ApiRoutesV1.imageUploadUrl);
  }

  newColor(color: string): void {
    this.editEmailSettingsForm.get('buttonColor').setValue(color);
  }

  uploadedLogoImage(file: FileInfo): void {
    this.emailSettings.logoUrl = file.url;
  }
}

@Component({
  selector: 'app-email-preview-dialog',
  templateUrl: 'email-preview-dialog.component.html',
  styleUrls: ['./preview-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EmailPreviewDialogComponent {
  @Input() emailType: Workflow;

  constructor(public dialogRef: MatDialogRef<EmailPreviewDialogComponent>) {}

  close(): void {
    this.dialogRef.close();
  }
}

@Component({
  selector: 'app-select-header-dialog',
  templateUrl: 'email-header-selection.html',
  styleUrls: ['email-header-selection.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SelectHeaderDialogComponent implements OnInit {
  displayUploadTab = true;
  imageUrls: string[] = [];
  aspects = [
    {
      label: 'Free',
      value: 0,
    },
    {
      label: '700:235 ',
      value: 700 / 235,
      selected: true,
    },
  ];
  @ViewChild('customImageUpload') customImageUpload: any;

  constructor(
    public dialogRef: MatDialogRef<SelectHeaderDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      uploadUrl: string;
      selectHeader: (url: string) => void;
    },
    public emailWorkflowService: EmailWorkflowService,
  ) {}

  ngOnInit(): void {
    if (!this.data.uploadUrl) {
      this.displayUploadTab = false;
    }
    this.emailWorkflowService.getDefaultImages().subscribe((response: HttpResponse<AjaxData>) => {
      this.imageUrls = response.body.data;
    });
  }

  customImageUploaded(file: FileInfo): void {
    this.data.selectHeader(file.url);
    this.dialogRef.close();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  selectImage(url: string): void {
    this.data.selectHeader(url);
    this.dialogRef.close();
  }

  close(): void {
    this.dialogRef.close();
  }
}
